<template>
  <div v-if="pageError" class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8 relative z-10">
      <div class="bg-white/5 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/10 text-center">
        <h2 class="text-2xl font-bold text-red-400 mb-4">Login Error</h2>
        <p class="text-slate-300 mb-4">{{ pageError }}</p>
        <button @click="reloadPage" class="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          Reload Page
        </button>
      </div>
    </div>
  </div>
  <div v-else class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
    <!-- Animated Background -->
    <div class="fixed inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-900/20 via-transparent to-transparent pointer-events-none"></div>
    <div class="fixed inset-0 bg-grid-pattern opacity-10 pointer-events-none"></div>
    
    <!-- Floating Particles -->
    <div class="fixed inset-0 pointer-events-none">
      <div class="particle particle-1"></div>
      <div class="particle particle-2"></div>
      <div class="particle particle-3"></div>
      <div class="particle particle-4"></div>
      <div class="particle particle-5"></div>
    </div>

    <div class="max-w-md w-full space-y-8 relative z-10">
      <!-- Login Card -->
      <div class="bg-white/5 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/10">
        <!-- Header -->
        <div class="text-center mb-8">
          <div class="flex justify-center mb-6">
            <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy Logo" class="w-16 h-16 rounded-2xl shadow-lg shadow-emerald-500/25" />
          </div>
          <h2 class="text-3xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent mb-2">
            Welcome back to OilBuddy AI
          </h2>
          <p class="text-slate-400">
            Sign in to your oilfield business dashboard
          </p>
        </div>

      <!-- Login Form -->
      <form class="mt-8 space-y-6" @submit.prevent>
        <div class="space-y-4">
          <!-- Email Field -->
          <div class="form-group">
            <label for="email" class="form-label">Email Address</label>
            <input
              id="email"
              name="email"
              v-model="form.email"
              type="email"
              required
              class="form-input"
              :class="{ 'border-danger-500': errors.email }"
              placeholder="Enter your email"
              autocomplete="email"
            />
            <p v-if="errors.email" class="text-danger-600 text-sm mt-1">
              {{ errors.email }}
            </p>
          </div>

          <!-- Password Field -->
          <div class="form-group">
            <label for="password" class="form-label">Password</label>
            <input
              id="password"
              name="password"
              v-model="form.password"
              type="password"
              required
              class="form-input"
              :class="{ 'border-danger-500': errors.password }"
              placeholder="Enter your password"
              autocomplete="current-password"
              @keyup.enter="handleLogin"
            />
            <p v-if="errors.password" class="text-danger-600 text-sm mt-1">
              {{ errors.password }}
            </p>
          </div>

          <!-- Remember Me & Forgot Password -->
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <input
                id="remember-me"
                v-model="form.rememberMe"
                type="checkbox"
                class="form-checkbox"
              />
              <label for="remember-me" class="ml-2 block text-sm text-oil-700">
                Remember me
              </label>
            </div>
            <div class="text-sm">
              <a href="#" class="font-medium text-primary-600 hover:text-primary-500">
                Forgot your password?
              </a>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div>
          <button
            type="button"
            :disabled="isLoading"
            class="btn-hero-primary w-full flex justify-center items-center"
            @click="handleLogin"
          >
            <svg
              v-if="isLoading"
              class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ isLoading ? 'Signing in...' : 'Sign In' }}
          </button>
        </div>

        <div class="demo-accounts">
          <p class="demo-heading">Demo accounts</p>
          <p class="demo-hint">Password for all demos: <span>demo123</span></p>
          <button
            v-for="demo in demoAccounts"
            :key="demo.email"
            type="button"
            class="demo-btn"
            @click="fillDemo(demo.email)"
          >
            {{ demo.label }} — {{ demo.email }}
          </button>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <div class="flex">
            <svg class="w-5 h-5 text-danger-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
            </svg>
            <p class="text-sm text-danger-700">{{ errorMessage }}</p>
          </div>
        </div>
      </form>

      <!-- Divider -->
      <div class="relative">
        <div class="absolute inset-0 flex items-center">
          <div class="w-full border-t border-slate-600/50"></div>
        </div>
        <div class="relative flex justify-center text-sm">
          <span class="px-2 bg-slate-800/50 text-slate-400">
            Or continue with
          </span>
        </div>
      </div>

      <!-- Social Login -->
      <div class="space-y-3">
        <button
          type="button"
          class="w-full flex justify-center items-center px-4 py-2 border border-slate-600/50 rounded-lg shadow-sm bg-slate-700/50 text-sm font-medium text-white hover:bg-slate-600/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
        >
          <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clip-rule="evenodd"/>
          </svg>
          Continue with GitHub
        </button>
      </div>

      <!-- Sign Up Link -->
      <div class="text-center">
        <p class="text-sm text-slate-400">
          Don't have an account?
          <a href="/#/register" class="font-medium text-emerald-400 hover:text-emerald-300">
            Sign up for free
          </a>
        </p>
        <p class="text-sm text-slate-400 mt-3">
          <a href="/#/admin/login" class="font-medium text-emerald-400 hover:text-emerald-300">
            Admin sign in
          </a>
        </p>
      </div>

      <!-- Oilfield-specific Info -->
      <div class="bg-slate-800/30 border border-slate-700/50 rounded-lg p-4">
        <div class="flex items-start">
          <svg class="w-5 h-5 text-emerald-400 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
          </svg>
          <div class="text-sm text-slate-300">
            <p class="font-medium text-white">Oilfield Professionals</p>
            <p class="mt-1">Access job marketplace, timesheets, and compliance tools designed specifically for contractors and companies in the ND oilfield.</p>
          </div>
        </div>
      </div>
      </div> <!-- Close login card -->
    </div>
  </div>
</template>

<script setup lang="ts">
import type { LoginForm } from '@/types'
import { reactive, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '../stores/userStore'
import { buildApiUrl } from '@/config/api'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()

// Error handling
const pageError = ref<string>('')

// Reload page function
const reloadPage = () => {
  window.location.reload()
}

// Global error handler for this page
const handlePageError = (error: any) => {
  console.error('LoginPage error:', error)
  pageError.value = error.message || 'An error occurred while loading the login page'
}

// Initialize page with error handling
onMounted(async () => {
  try {
    console.log('LoginPage mounted successfully')
    
    // Check if user is already authenticated and redirect if needed
    const token = localStorage.getItem('auth_token')
    const userType = localStorage.getItem('user_type')
    
    if (token && userType && userType !== 'admin') {
      console.log('User already authenticated, redirecting...')
      if (userType === 'company') {
        await router.push('/company')
      } else if (userType === 'employee') {
        const crew = localStorage.getItem('crew_level') || ''
        await router.push(['supervisor_1', 'supervisor_2', 'supervisor_3'].includes(crew) ? '/supervisor' : '/employee')
      } else if (userType === 'company_man') {
        await router.push('/company-man')
      } else if (userType === 'driver') {
        await router.push('/driver')
      } else {
        await router.push('/dashboard')
      }
    }
  } catch (error) {
    handlePageError(error)
  }
})

// Form state
const form = reactive<LoginForm>({
  email: '',
  password: '',
  rememberMe: false
})

// UI state
const isLoading = ref(false)
const errorMessage = ref('')
const errors = reactive({
  email: '',
  password: ''
})

const demoAccounts = [
  { label: 'Employee', email: 'employee@demo.com' },
  { label: 'Supervisor', email: 'supervisor@demo.com' },
  { label: 'Company Man', email: 'companyman@demo.com' },
  { label: 'Contractor', email: 'contractor@demo.com' },
  { label: 'Driver', email: 'driver@demo.com' },
  { label: 'Company', email: 'company@demo.com' },
]

function fillDemo(email: string) {
  form.email = email
  form.password = 'demo123'
  errors.email = ''
  errors.password = ''
  errorMessage.value = ''
}

// Validation
const validateForm = (): boolean => {
  errors.email = ''
  errors.password = ''
  
  if (!form.email) {
    errors.email = 'Email is required'
    return false
  }
  
  if (!form.email.includes('@')) {
    errors.email = 'Please enter a valid email address'
    return false
  }
  
  if (!form.password) {
    errors.password = 'Password is required'
    return false
  }
  
  if (form.password.length < 6) {
    errors.password = 'Password must be at least 6 characters'
    return false
  }
  
  return true
}

// Handle login
const handleLogin = async (event?: Event) => {
  // Ensure form doesn't submit
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }
  
  if (!validateForm()) {
    return
  }
  
  isLoading.value = true
  errorMessage.value = ''
  
  try {
    console.log('Making login request to login.php')
    
    // Use the buildApiUrl utility for correct API endpoint
    const response = await fetch(buildApiUrl('login.php'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: form.email,
        password: form.password
      })
    })

    console.log('Response status:', response.status)
    console.log('Response headers:', response.headers)
    
    // Check if response is ok
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    // Get response text first to debug
    const responseText = await response.text()
    console.log('Response text:', responseText)
    
    // Try to parse JSON
    let data
    try {
      data = JSON.parse(responseText)
    } catch (parseError) {
      console.error('JSON parse error:', parseError)
      throw new Error(`Invalid JSON response: ${responseText}`)
    }

    if (data.success) {
      // User door must not inherit or create an admin session
      localStorage.removeItem('adminToken')
      localStorage.removeItem('adminUser')
      localStorage.removeItem('is_admin')
      const appUserType = data.user.userType === 'admin' ? 'contractor' : data.user.userType
      data.user.userType = appUserType
      // Store user data and authentication token
      localStorage.setItem('auth_token', data.token || 'authenticated_user_token')
      localStorage.setItem('user_type', appUserType)
      localStorage.setItem('user_data', JSON.stringify(data.user))
      if (data.user.crewLevel) {
        localStorage.setItem('crew_level', data.user.crewLevel)
      }
      
      console.log('Stored authentication data:', {
        token: localStorage.getItem('auth_token'),
        userType: localStorage.getItem('user_type'),
        userData: localStorage.getItem('user_data')
      })
      
      // Load the user into the store and wait for it to complete
      await userStore.loadUserFromStorage()
      
      console.log('Login successful:', data)
      console.log('User store updated:', userStore.isAuthenticated, userStore.currentUser)
      
      // Redirect to appropriate dashboard using proper SPA navigation
      const requestedRedirect = typeof route.query.redirect === 'string' ? route.query.redirect : ''
      const defaultRoute = data.user.userType === 'company'
        ? '/company'
        : data.user.userType === 'employee'
          ? (['supervisor_1', 'supervisor_2', 'supervisor_3'].includes(data.user.crewLevel || '') ? '/supervisor' : '/employee')
          : data.user.userType === 'company_man'
            ? '/company-man'
            : data.user.userType === 'driver'
              ? '/driver'
              : '/dashboard'
      const targetRoute = requestedRedirect.startsWith('/') && !requestedRedirect.startsWith('/admin')
        ? requestedRedirect
        : defaultRoute
      console.log('Navigating to:', targetRoute)
      
      // Use Vue Router for SPA navigation (no page reload)
      try {
        await router.push(targetRoute)
        console.log('SPA navigation successful to:', targetRoute)
      } catch (error) {
        console.error('SPA navigation failed:', error)
        // Only use fallback if Vue Router fails
        window.location.href = `/#${targetRoute}`
      }
    } else {
      // Handle API error response
      errorMessage.value = data.error || 'Invalid email or password'
    }
  } catch (error) {
    console.error('Login error:', error)
    
    // Type guard for error handling
    const errorObj = error as Error
    console.error('Error details:', {
      name: errorObj.name,
      message: errorObj.message,
      stack: errorObj.stack
    })
    
    if (errorObj.name === 'TypeError' && errorObj.message.includes('fetch')) {
      errorMessage.value = 'Network error: Unable to connect to the server. Please check if the API is running.'
    } else {
      errorMessage.value = `Network error occurred: ${errorObj.message}`
    }
    
    // Fallback to demo login for backward compatibility
    if (form.email === 'demo@oilbuddyai.com' && form.password === 'password') {
      console.log('Using demo login fallback')
      // Store auth token and create basic user data
      localStorage.setItem('auth_token', 'demo_token_123')
      localStorage.setItem('user_type', 'contractor')
      
      // Create user data for the store
      const userData = {
        id: 'demo',
        name: 'Demo User',
        email: form.email,
        userType: 'contractor' as const,
        jobTitle: 'Flowback Operator',
        location: 'Williston, ND'
      }
      localStorage.setItem('user_data', JSON.stringify(userData))
      
      // Load the user into the store and wait for it to complete
      await userStore.loadUserFromStorage()
      
      console.log('Demo login successful, user store updated:', userStore.isAuthenticated, userStore.currentUser)
      
      // Use proper SPA navigation for demo login
      try {
        await router.push('/dashboard')
        console.log('Demo SPA navigation successful to /dashboard')
      } catch (error) {
        console.error('Demo SPA navigation failed:', error)
        window.location.href = '/#/dashboard'
      }
    }
  }
  
  isLoading.value = false
}
</script>

<style scoped>
/* Background patterns and animations */
.bg-grid-pattern {
  background-image: radial-gradient(circle, rgba(148, 163, 184, 0.3) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: grid-move 20s linear infinite;
}

@keyframes grid-move {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

/* Floating particles */
.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: linear-gradient(45deg, #10b981, #06b6d4);
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

.particle-1 { top: 20%; left: 10%; animation-delay: 0s; }
.particle-2 { top: 60%; left: 20%; animation-delay: 1s; }
.particle-3 { top: 40%; left: 80%; animation-delay: 2s; }
.particle-4 { top: 80%; left: 70%; animation-delay: 3s; }
.particle-5 { top: 30%; left: 60%; animation-delay: 4s; }

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.6; }
  50% { transform: translateY(-20px) rotate(180deg); opacity: 1; }
}

/* Form styles */
.form-group {
  @apply space-y-2;
}

.form-label {
  @apply block text-sm font-medium text-slate-300;
}

.form-input {
  @apply w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all duration-300;
}

/* Button styles */
.btn-hero-primary {
  @apply w-full inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25;
}

.btn-outline {
  @apply w-full inline-flex items-center justify-center px-6 py-3 border border-slate-600 text-slate-300 font-medium rounded-xl hover:bg-slate-700/50 transition-colors duration-200;
}

.demo-accounts {
  @apply space-y-2 pt-1;
}

.demo-heading {
  @apply text-sm font-bold text-white;
}

.demo-hint {
  @apply text-xs text-slate-400;
}

.demo-hint span {
  @apply text-emerald-300 font-semibold;
}

.demo-btn {
  @apply w-full text-left px-4 py-2.5 border border-slate-600/70 rounded-xl text-xs text-slate-200 bg-transparent hover:bg-slate-700/50 hover:border-emerald-500/40 transition-colors;
}

/* Text color overrides */
.text-oil-900 {
  @apply text-white;
}

.text-oil-600 {
  @apply text-slate-400;
}

.text-oil-700 {
  @apply text-slate-300;
}

.text-primary-600 {
  @apply text-emerald-400;
}

.text-danger-600 {
  @apply text-red-400;
}

.border-danger-500 {
  @apply border-red-500;
}

.hover\:text-primary-600:hover {
  @apply hover:text-emerald-400;
}

/* Additional theme overrides */
.bg-oil-100 {
  @apply bg-slate-800/30 border border-slate-700/50;
}

.text-oil-500 {
  @apply text-slate-400;
}

.border-oil-300 {
  @apply border-slate-600/50;
}

.hover\:text-primary-500:hover {
  @apply hover:text-emerald-300;
}

.text-primary-500 {
  @apply text-emerald-300;
}

.bg-danger-50 {
  @apply bg-red-900/20;
}

.border-danger-200 {
  @apply border-red-700/50;
}

.text-danger-700 {
  @apply text-red-300;
}

.text-danger-400 {
  @apply text-red-400;
}
</style>
